import _ from 'lodash';

import { accessRole, TAccessRole } from 'src/app/infrastructure/models/access-role';
import { domainUserDisplayName, organisationLabel, userDisplayName } from 'src/app/infrastructure/functions/user-name.function';
import { ILevelDomainData, levelDomainData } from 'src/app/infrastructure/models/level-domain-data';
import { IDomainUserForm } from './domain-user-form';
import { DomainData, IDomainData } from '../../domain/models/domain-data';
import { IUserData, UserData } from '../../user/models/user-data';
import { IDomainUserDto, domainUserDto } from 'src/assets/common/models/domain-user-dto.g';
import { IImageBoxData, ImageBoxData, imageBoxData } from '../../image/models/image-box-data';
import { imageData } from '../../image/models/image-data';
import { IImageParentData, imageParentData } from '../../image/models/image-parent-data';
import { userDto } from 'src/assets/common/models/user-dto.g';
import { TMembershipLock } from 'src/assets/common/enums/membership-lock.g';
import { affiliation, TAffiliation } from 'src/assets/common/enums/affiliation.g';
import { dayToLocalDate, momentPassed } from 'src/app/infrastructure/functions/zone-day.function';
import { membershipClosed } from '../../function-member/models/membership-access-dates';

export const domainUserData = {
    ...levelDomainData,
    ...imageParentData,
    guid: 'guid',
    nr: 'nr',
    // Optionele naam binnen het domain (vaak leeg). Voor weergave/zoeken/sorteren displayName gebruiken.
    name: 'name',
    // name → user_displayName (firstFilled, zie user-name.function.ts). Zelfde sleutel als
    // domainUserListData.displayName, om dezelfde reden als user_email hieronder.
    displayName: 'displayName',
    // Terugval als name leeg is: user.applicationName → firstName + lastName → email (userDisplayName).
    // Placeholder van het naamveld in Edit.
    user_displayName: 'user_displayName',
    user_guid: 'userGuid',
    isAdmin: 'isAdmin',
    isOwner: 'isOwner',
    lastVisit: 'lastVisit',
    // Kalenderdagen in de zone van het domain (backend: StartDay/EndDay; endDate = laatste dag mét
    // toegang) als lokale Date voor weergave en datepicker. startDay/endDay/timeZoneId voor de datumregels.
    startDate: 'startDate',
    endDate: 'endDate',
    startDay: 'startDay',
    endDay: 'endDay',
    timeZoneId: 'timeZoneId',
    deleted: 'deleted',
    inactive: 'inactive',
    // DomainUser zelf heeft geen e-mailveld — komt uit de genestelde User (zie userData hieronder) —
    // vandaar de user_-prefix, zelfde reden als user_guid en zelfde precedent als
    // domainUserListData.user_email. Hier alsnog als eigen top-level veld gecompileerd (i.p.v. alleen
    // via .userData?.[userData.email] te ontsluiten) zodat DomainComponent's searchFields op de
    // DomainUsers-tab hierop kan matchen — de naam moet dus letterlijk gelijk zijn aan
    // domainUserListData.user_email, de property die de tablist-rijen daadwerkelijk dragen.
    user_email: 'user_email',
    // Afgeleid (net als domainUserListData.active): niet zelf op de dto aanwezig, maar berekend uit
    // inactive + de verstreken periode (zie constructor) — zelfde semantiek als domain-user-list-data.ts.
    // Zelfde sleutel als daar, om dezelfde reden als user_email hierboven.
    active: 'active',
    // Zelfde sleutel/waarden als domainUserListData.accessRole, om dezelfde reden als user_email
    // hierboven (DomainComponent's searchFields/sortFields matchen op de string-waarde).
    accessRole: 'accessRole',
    // Einddatum vergrendeld voor de aanroeper (owner / beheerfunctie die hij niet mag toekennen), door de
    // backend berekend (DomainUserDto.endDateLock) — DomainUserEdit toont het veld dan readonly met hint.
    endDateLock: 'endDateLock',
    // Gast/vast (DomainUser.Permanent); guestEndAuto = de einddatum komt van de gastregel (geen project meer).
    permanent: 'permanent',
    guestEndAuto: 'guestEndAuto',
    // Organisatie waarvoor het lid werkt (DomainUser.Affiliation): eigen organisatie, een contact, of onbekend.
    // organisation_name = domainnaam of contactnaam (backend); organisationContact_guid alleen bij een contact.
    affiliation: 'affiliation',
    organisationContact_guid: 'organisationContact_guid',
    organisation_name: 'organisation_name',
    // Weergave naast de naam: 'intern' of de contactnaam (organisationLabel); null = onbekend.
    organisation_label: 'organisation_label',
    // Lidmaatschap beëindigd (eindmoment voorbij, zie membershipClosed): alleen-lezen tot Toegang herstellen.
    closed: 'closed',

    domainData: 'domainData',
    userData: 'userData',
    domainPhotoData: 'domainPhotoBoxData',
    profilePhotoData: 'profilePhotoBoxData',
    userPhotoData: 'userPhotoBoxData',
} as const;

export type TDomainUserData = (typeof domainUserData)[keyof typeof domainUserData];

export interface IDomainUserData extends ILevelDomainData, IImageParentData {
    [domainUserData.guid]: string;
    [domainUserData.nr]: number | null;
    [domainUserData.name]: string | null;
    [domainUserData.displayName]: string;
    [domainUserData.user_displayName]: string;
    [domainUserData.isAdmin]: boolean | null;
    [domainUserData.isOwner]: boolean | null;
    [domainUserData.lastVisit]: Date | null;
    [domainUserData.startDate]: Date | null;
    [domainUserData.endDate]: Date | null;
    [domainUserData.startDay]: string | null;
    [domainUserData.endDay]: string | null;
    [domainUserData.timeZoneId]: string | null;
    [domainUserData.deleted]: boolean;
    [domainUserData.inactive]: boolean;

    [domainUserData.user_guid]: string | null;
    [domainUserData.user_email]: string | null;
    [domainUserData.active]: boolean;
    [domainUserData.accessRole]: TAccessRole;
    [domainUserData.endDateLock]: TMembershipLock | null;
    [domainUserData.permanent]: boolean;
    [domainUserData.guestEndAuto]: boolean;
    [domainUserData.affiliation]: TAffiliation;
    [domainUserData.organisationContact_guid]: string | null;
    [domainUserData.organisation_name]: string | null;
    [domainUserData.organisation_label]: string | null;
    [domainUserData.closed]: boolean;

    [domainUserData.domainData]: IDomainData | null;
    [domainUserData.userData]: IUserData | null;
    [domainUserData.domainPhotoData]: IImageBoxData | null;
    [domainUserData.profilePhotoData]: IImageBoxData | null;
    [domainUserData.userPhotoData]: IImageBoxData | null;
}

export class DomainUserData
    implements IDomainUserData, ILevelDomainData
{
    [domainUserData.guid]!: string;
    [domainUserData.nr]: number | null = null;
    [domainUserData.name]: string | null = null;
    [domainUserData.displayName]: string = '';
    [domainUserData.user_displayName]: string = '';
    [domainUserData.isAdmin]: boolean | null = null;
    [domainUserData.isOwner]: boolean | null = null;
    [domainUserData.lastVisit]: Date | null = null;
    [domainUserData.startDate]: Date | null = null;
    [domainUserData.endDate]: Date | null = null;
    [domainUserData.startDay]: string | null = null;
    [domainUserData.endDay]: string | null = null;
    [domainUserData.timeZoneId]: string | null = null;
    [domainUserData.deleted]!: boolean;
    [domainUserData.inactive]!: boolean;

    [domainUserData.user_guid]: string | null = null;
    [domainUserData.user_email]: string | null = null;
    [domainUserData.active]: boolean = true;
    [domainUserData.accessRole]: TAccessRole = accessRole.user;
    [domainUserData.endDateLock]: TMembershipLock | null = null;
    [domainUserData.permanent]: boolean = true;
    [domainUserData.guestEndAuto]: boolean = false;
    [domainUserData.affiliation]: TAffiliation = affiliation.unknown;
    [domainUserData.organisationContact_guid]: string | null = null;
    [domainUserData.organisation_name]: string | null = null;
    [domainUserData.organisation_label]: string | null = null;
    [domainUserData.closed]: boolean = false;

    // IImageParentData: hoofdfoto van dit DomainUser-record (banner-box), bron = profilePhotoData hieronder.
    [domainUserData.photoThumbGuid]: string | null = null;
    [domainUserData.photoOriginGuid]: string | null = null;
    [domainUserData.photoVersion]: string | null = null;

    [domainUserData.domainData]: IDomainData | null = null;
    [domainUserData.userData]: IUserData | null = null;
    [domainUserData.domainPhotoData]: IImageBoxData | null = null;
    [domainUserData.profilePhotoData]: IImageBoxData | null = null;
    [domainUserData.userPhotoData]: IImageBoxData | null = null;
    
    protected constructor(modelDto?: IDomainUserDto | null);
    protected constructor(modelData: IDomainUserData, modelForm: IDomainUserForm);
    protected constructor(value?: IDomainUserDto | IDomainUserData | null, modelForm?: IDomainUserForm) {
        if (!!modelForm) {
            const _modelData = value as IDomainUserData;
            _.assign(this, _modelData, modelForm);
            this[domainUserData.displayName] = domainUserDisplayName(this[domainUserData.name], this[domainUserData.user_displayName]);
        } else if (!!value) {
            const _modelDto = value as IDomainUserDto;
            const _lastVisit = _modelDto?.[domainUserDto.lastVisit];
            
            this[domainUserData.guid] = _modelDto[domainUserDto.guid]!;
            this[domainUserData.nr] = _modelDto[domainUserDto.nr];
            this[domainUserData.name] = _modelDto[domainUserDto.name];
            this[domainUserData.isAdmin] = _modelDto[domainUserDto.isAdmin];
            this[domainUserData.isOwner] = _modelDto[domainUserDto.isOwner];
            // Zelfde tekst/precedentie als domainUserListData.accessRole (domain-user-list-data.ts).
            this[domainUserData.accessRole] = this[domainUserData.isOwner] ? accessRole.owner
                : this[domainUserData.isAdmin] ? accessRole.administrator
                : accessRole.user;
            this[domainUserData.lastVisit] = _lastVisit != null ? new Date(_lastVisit) : null;
            this[domainUserData.startDay] = _modelDto[domainUserDto.startDay] ?? null;
            this[domainUserData.endDay] = _modelDto[domainUserDto.endDay] ?? null;
            this[domainUserData.timeZoneId] = _modelDto[domainUserDto.timeZoneId] ?? null;
            this[domainUserData.startDate] = dayToLocalDate(this[domainUserData.startDay]);
            this[domainUserData.endDate] = dayToLocalDate(this[domainUserData.endDay]);
            this[domainUserData.deleted] = _modelDto[domainUserDto.deleted]!;
            this[domainUserData.inactive] = _modelDto[domainUserDto.inactive]!;
            this[domainUserData.endDateLock] = _modelDto[domainUserDto.endDateLock] ?? null;
            this[domainUserData.permanent] = _modelDto[domainUserDto.permanent] ?? true;
            this[domainUserData.guestEndAuto] = _modelDto[domainUserDto.guestEndAuto] ?? false;
            this[domainUserData.affiliation] = _modelDto[domainUserDto.affiliation] ?? affiliation.unknown;
            this[domainUserData.organisationContact_guid] = _modelDto[domainUserDto.organisationContact_Guid] ?? null;
            this[domainUserData.organisation_name] = _modelDto[domainUserDto.organisation_Name] ?? null;
            this[domainUserData.organisation_label] = organisationLabel(this[domainUserData.affiliation], this[domainUserData.organisation_name]);

            this[domainUserData.user_guid] = _modelDto[domainUserDto.userDto]?.[userDto.guid] ?? null;
            this[domainUserData.user_email] = _modelDto[domainUserDto.userDto]?.[userDto.email] ?? null;
            this[domainUserData.user_displayName] = userDisplayName(_modelDto[domainUserDto.userDto]);
            this[domainUserData.displayName] = domainUserDisplayName(this[domainUserData.name], this[domainUserData.user_displayName]);

            // De dto draagt het exclusieve eindmoment (EndDate, UTC): verlopen = EndDate <= nu, tijdzonevrij en
            // gelijk aan de backend (zie ZoneClock). inactive bevat dat ook al; dit vangt alleen een pagina
            // af die openstaat terwijl het moment verstrijkt.
            this[domainUserData.active] = !momentPassed(_modelDto[domainUserDto.endDate]) && !this[domainUserData.inactive];
            this[domainUserData.closed] = membershipClosed(_modelDto[domainUserDto.endDate]);

            this[domainUserData.domainData] = DomainData.create(_modelDto[domainUserDto.domainDto]);
            this[domainUserData.userData] = UserData.create(_modelDto[domainUserDto.userDto]);
            this[domainUserData.domainPhotoData] = ImageBoxData.create(_modelDto[domainUserDto.domainPhotoDto]);
            this[domainUserData.userPhotoData] = ImageBoxData.create(_modelDto[domainUserDto.userPhotoDto]);

            // Fallback: geen domain-specifieke profielfoto gekozen (DomainUser.ProfileFileBoxId) →
            // toon de Home-hoofdfoto van de User (User.PhotoFileBoxId) i.p.v. niets.
            const _profilePhoto = ImageBoxData.create(_modelDto[domainUserDto.profilePhotoDto] ?? _modelDto[domainUserDto.userPhotoDto]);
            this[domainUserData.profilePhotoData] = _profilePhoto;
            this[domainUserData.photoThumbGuid] = _profilePhoto?.[imageBoxData.photoThumbData]?.[imageData.guid] ?? null;
            this[domainUserData.photoOriginGuid] = _profilePhoto?.[imageBoxData.photoData]?.[imageData.guid] ?? null;
            this[domainUserData.photoVersion] = _profilePhoto?.[imageBoxData.photoThumbData]?.[imageData.modifiedOn] ?? null;
        }
    }
        
    static create(modelDto?: IDomainUserDto | null): IDomainUserData | null
    static create(modelData: IDomainUserData, modelForm: IDomainUserForm): IDomainUserData | null
    static create(value?: IDomainUserDto | IDomainUserData | null, modelForm?: IDomainUserForm): IDomainUserData | null {

        if (!!modelForm) {
            const _modelData = value as IDomainUserData;
            return new DomainUserData(_modelData, modelForm);
        } else {
            const _modelDto = value as IDomainUserDto;
            return _modelDto ? new DomainUserData(_modelDto) : null;
        }
    }
}
